import { memo, useMemo } from "react";
import type * as type from "@vTypes";
import NurseRosterList from "@components/feature/rosterTable/row_NurseRosterList";
import TableBody_base from "@components/feature/rosterTable/TableBody_base";
import type { PrevTailData } from "@/hooks/roster-craete/useRosterPrevTail";
import type {
  EmergencyBulkPeriodPick,
  EmergencyPostApplyHighlight,
  EmergencyReplacementSlotPick,
} from "@/types/emergency-replacement.types";
import { RosterTableBodyChrome } from "@/components/feature/rosterTable/RosterTableBodyChrome";
import TableBodyView, {
  type TableBodyViewProps,
} from "@/components/feature/roster_view/TableBodyView";

export type RosterCreateTableBodyProps = {
  copyNurses: unknown[];
  setCopyNurses: (value: unknown) => void;
  availableShifts: type.availableShifts[];
  isEditMode: boolean;
  selectedShift: type.Type_availableShifts | null;
  modDayShift: (
    nurseIndex: number,
    scheduleDay: number,
    changeShift: string,
    cellShiftDbId?: number | null
  ) => void;
  saveNurseSequence: (nurse_id: string, newSequence: number) => Promise<void>;
  currentScheduleInfo:
    | { year: number; month: number; version: number }
    | undefined;
  currentScheduleID: string | undefined;
  isDragEditing: boolean;
  selectedGroupId?: string | null;
  manPower_monthly: type.ManPowerMonthly | null;
  manPower_daily?: type.ManPowerDaily | null;
  useMaxManpower?: boolean;
  enableMaxManpowerViolation?: boolean;
  year: number;
  month: number;
  selectIdForSideprofile?: (bool: boolean, nurseID: string) => void;
  disableManpowerValidation?: boolean;
  onlyView?: boolean;
  enableRowDragSort?: boolean;
  /** true: 원티드 모달 등 — tbody 카드 세로 패딩 축소 */
  compactVerticalPadding?: boolean;
  prevTailData?: PrevTailData | null;
  isEmergencyPickMode?: boolean;
  onEmergencySlotPick?: (pick: EmergencyReplacementSlotPick) => void;
  emergencySlotPick?: EmergencyReplacementSlotPick | null;
  isEmergencyBulkPickMode?: boolean;
  onEmergencyBulkPick?: (pick: {
    nurseId: string;
    nurseName: string;
    dayIndex: number;
    dateISO: string;
    shiftColor?: string;
  }) => void;
  emergencyBulkPeriodPick?: EmergencyBulkPeriodPick | null;
  emergencyBulkSelectedSteps?: Array<{ date: string; nurseId: string }> | null;
  emergencyReplacementCandidateNurseId?: string | null;
  emergencyPostApplyHighlight?: EmergencyPostApplyHighlight | null;
  showPerShiftStats?: boolean;
  violationVisibilityOverride?: {
    showConfigViolations: boolean;
    showManpowerViolations: boolean;
  } | null;
  /** false: 셀·헤더 클릭으로 열·행 선택(클릭 하이라이트) 안 함 */
  selectionFocusOnClick?: boolean;
  /** `/roster/schedule` 응답의 year/month — 뷰의 year/month 와 같을 때만 inbound 바 표시 */
  schedulePayloadYear?: number;
  schedulePayloadMonth?: number;
  /** 현재 편집 중인 병동 group_id — inbound 잠금 정책에 사용 */
  currentGroupId?: string | null;
};

export type TableBodyProps =
  | ({ mode: "CREATE" } & RosterCreateTableBodyProps)
  | ({ mode: "VIEW" } & TableBodyViewProps);

const TableBodyCreate = memo(function TableBodyCreate({
  copyNurses,
  setCopyNurses,
  availableShifts,
  isEditMode,
  selectedShift,
  modDayShift,
  saveNurseSequence,
  currentScheduleInfo,
  currentScheduleID,
  isDragEditing,
  selectedGroupId,
  manPower_monthly,
  manPower_daily,
  useMaxManpower = false,
  enableMaxManpowerViolation = true,
  year,
  month,
  selectIdForSideprofile,
  disableManpowerValidation = false,
  onlyView = false,
  enableRowDragSort = true,
  compactVerticalPadding = false,
  prevTailData,
  isEmergencyPickMode = false,
  onEmergencySlotPick,
  emergencySlotPick = null,
  isEmergencyBulkPickMode = false,
  onEmergencyBulkPick,
  emergencyBulkPeriodPick = null,
  emergencyBulkSelectedSteps = null,
  emergencyReplacementCandidateNurseId = null,
  emergencyPostApplyHighlight = null,
  showPerShiftStats = false,
  violationVisibilityOverride,
  selectionFocusOnClick = true,
  schedulePayloadYear,
  schedulePayloadMonth,
  currentGroupId,
}: RosterCreateTableBodyProps) {
  const daysInMonth = useMemo(() => {
    if (!year || month < 1 || month > 12) return 0;
    return new Date(year, month, 0).getDate();
  }, [year, month]);

  return (
    <>
      {copyNurses && copyNurses.length > 0 ? (
        <RosterTableBodyChrome
          daysInMonth={daysInMonth}
          hasPrevTail={!!prevTailData}
          compactVerticalPadding={compactVerticalPadding}
        >
          <TableBody_base
            copyNurses={copyNurses}
            setCopyNurses={setCopyNurses}
            availableShifts={availableShifts}
            isEditMode={isEditMode}
            selectedShift={selectedShift}
            modDayShift={modDayShift}
            saveNurseSequence={saveNurseSequence}
            currentScheduleInfo={currentScheduleInfo}
            currentScheduleID={currentScheduleID}
            isDragEditing={isDragEditing}
            selectedGroupId={selectedGroupId}
            manPower_monthly={manPower_monthly}
            manPower_daily={manPower_daily}
            useMaxManpower={useMaxManpower}
            enableMaxManpowerViolation={enableMaxManpowerViolation}
            year={year}
            month={month}
            selectIdForSideprofile={selectIdForSideprofile}
            disableManpowerValidation={disableManpowerValidation}
            onlyView={onlyView}
            enableRowDragSort={enableRowDragSort}
            RowComponent={NurseRosterList}
            prevTailData={prevTailData}
            isEmergencyPickMode={isEmergencyPickMode}
            onEmergencySlotPick={onEmergencySlotPick}
            emergencySlotPick={emergencySlotPick}
            isEmergencyBulkPickMode={isEmergencyBulkPickMode}
            onEmergencyBulkPick={onEmergencyBulkPick}
            emergencyBulkPeriodPick={emergencyBulkPeriodPick}
            emergencyBulkSelectedSteps={emergencyBulkSelectedSteps}
            emergencyReplacementCandidateNurseId={
              emergencyReplacementCandidateNurseId
            }
            emergencyPostApplyHighlight={emergencyPostApplyHighlight}
            showPerShiftStats={showPerShiftStats}
            violationVisibilityOverride={violationVisibilityOverride}
            selectionFocusOnClick={selectionFocusOnClick}
            schedulePayloadYear={schedulePayloadYear}
            schedulePayloadMonth={schedulePayloadMonth}
            currentGroupId={currentGroupId}
          />
        </RosterTableBodyChrome>
      ) : (
        <div className="py-10 text-zinc-500">등록된 인원이 없습니다.</div>
      )}
    </>
  );
});

function TableBody(props: TableBodyProps) {
  if (props.mode === "VIEW") {
    const { mode, ...viewProps } = props;
    void mode;
    return <TableBodyView {...viewProps} />;
  }
  const { mode, ...createProps } = props;
  void mode;
  return <TableBodyCreate {...createProps} />;
}

export default memo(TableBody);
